<template>
  <v-container fluid>
    <section>
      <v-card rounded="lg" outlined>
        <v-card-text>
          <div class="row justify-content-between align-items-center">
            <div class="col-12 col-lg-8">
              <v-card-title class="px-0 pt-0">
                {{ $t("manage_buffet_plans") }}
              </v-card-title>
              <p class="text-body-2 grey--text mb-0 pr-2">
                {{ $t("manage_buffet_plans_desc") }}
              </p>
            </div>
            <div
              class="col-12 col-lg-4 d-flex flex-wrap justify-end mt-3 mt-lg-0"
              style="gap: 8px"
            >
              <v-tooltip bottom :disabled="!!branchId">
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    <v-btn
                      depressed
                      outlined
                      color="primary"
                      class="text-capitalize"
                      :disabled="store_type !== 'BUFFET' || !branchId"
                      @click="openStoreQr"
                    >
                      <v-icon left small>fal fa-qrcode</v-icon>
                      {{ $t("store_buffet_qr") }}
                    </v-btn>
                  </span>
                </template>
                <span>{{ $t("store_buffet_qr_branch_required") }}</span>
              </v-tooltip>
              <v-btn
                depressed
                color="primary"
                class="white--text"
                :disabled="store_type !== 'BUFFET'"
                @click="openCreate"
              >
                <v-icon left>fal fa-plus-circle</v-icon>
                {{ $t("new_data") }}
              </v-btn>
            </div>
          </div>

          <v-alert
            v-if="store_type !== 'BUFFET'"
            dense
            type="info"
            outlined
            class="mt-4 mb-0"
          >
            {{ $t("buffet_manage_require_buffet_store") }}
          </v-alert>

          <v-data-table
            v-else
            :headers="headers"
            :items="items"
            :loading="loading"
            item-key="id"
            class="elevation-0 mt-4"
            :no-data-text="$t('no_data')"
            :loading-text="$t('loading')"
            :footer-props="{
              itemsPerPageOptions: rowsPerPageItems,
            }"
          >
            <template v-slot:item.index="{ index }">
              {{ index + 1 }}
            </template>
            <template v-slot:item.price="{ item }">
              {{ item.price | NumberFormat }}
            </template>
            <template v-slot:item.categories="{ item }">
              <span class="text-caption">{{
                categorySummary(item.categories)
              }}</span>
            </template>
            <template v-slot:item.is_active="{ item }">
              <v-chip
                small
                label
                dark
                class="text-capitalize"
                :color="item.is_active ? 'green' : 'grey'"
              >
                {{ item.is_active ? $t("active") : $t("inactive") }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    fab
                    x-small
                    depressed
                    class="rounded-lg"
                    :aria-label="$t('description')"
                    @click="viewItem(item)"
                  >
                    <v-icon color="blue" x-small>fal fa-eye</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("description") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    fab
                    x-small
                    depressed
                    class="rounded-lg mx-2"
                    :aria-label="$t('edit')"
                    @click="openEdit(item)"
                  >
                    <v-icon color="green" x-small>fal fa-edit</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("edit") }}</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    fab
                    x-small
                    depressed
                    class="rounded-lg"
                    :aria-label="$t('delete')"
                    @click="remove(item)"
                  >
                    <v-icon color="red" x-small>fal fa-trash</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("delete") }}</span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- View -->
      <app-custom-dialog
        v-model="dialog"
        :footer="false"
        title="description"
        width="560px"
      >
        <div slot="activator">
          <v-row>
            <v-col cols="4" class="grey--text">{{ $t("id") }}</v-col>
            <v-col cols="8" class="text-right font-weight-medium">{{
              viewForm.id
            }}</v-col>
            <v-col cols="4" class="grey--text">{{
              $t("buffet_plan_name")
            }}</v-col>
            <v-col cols="8" class="text-right">{{
              viewForm.name || "—"
            }}</v-col>
            <v-col cols="4" class="grey--text">{{
              $t("buffet_plan_price")
            }}</v-col>
            <v-col cols="8" class="text-right">
              <template v-if="viewForm.price != null && viewForm.price !== ''">
                {{ viewForm.price | NumberFormat }}
              </template>
              <template v-else>—</template>
            </v-col>
            <v-col cols="4" class="grey--text">{{
              $t("buffet_plan_categories")
            }}</v-col>
            <v-col cols="12" class="text-right">
              <v-chip
                v-for="c in viewForm.categories || []"
                :key="c.id"
                small
                label
                outlined
                color="primary"
                class="ma-1"
              >
                {{ categoryLabel(c) }}
              </v-chip>
              <span
                v-if="!viewForm.categories || !viewForm.categories.length"
                class="text-caption grey--text"
                >—</span
              >
            </v-col>
            <v-col cols="4" class="grey--text">{{ $t("status") }}</v-col>
            <v-col cols="8" class="text-right">
              <v-chip
                small
                label
                dark
                :color="viewForm.is_active ? 'green' : 'grey'"
              >
                {{ viewForm.is_active ? $t("active") : $t("inactive") }}
              </v-chip>
            </v-col>
            <v-col cols="4" class="grey--text">{{
              $t("buffet_sort_order")
            }}</v-col>
            <v-col cols="8" class="text-right">{{
              viewForm.sort_order ?? "—"
            }}</v-col>
          </v-row>
        </div>
      </app-custom-dialog>

      <!-- Create / Edit -->
      <app-custom-dialog
        v-model="dialogAdd"
        :footer="true"
        :title="titleStatus"
        submit-text="save"
        width="560px"
        @submit="save"
      >
        <div slot="activator">
          <TTextField
            label="buffet_plan_name"
            :errorMessages="nameErrors"
            v-model="form.name"
            @input="touchName"
            @blur="touchName"
          />
          <CurrencyInput
            class="mt-3"
            label="buffet_plan_price"
            v-model="form.price"
            :suffix="currency"
          />
          <TextSelect
            class="mt-4"
            label="buffet_plan_categories"
            :errorMessages="[]"
            v-model="form.category_ids"
            itemValue="id"
            text="la_name"
            :items="categorySelectItems"
            :return-object="false"
            :multiple="true"
            :chips="true"
            :disabled="store_type !== 'BUFFET'"
          />
          <v-switch
            v-model="form.is_active"
            class="mt-3"
            inset
            hide-details="auto"
            :label="$t('active')"
          />
          <TTextField
            class="mt-3"
            label="buffet_sort_order"
            :errorMessages="[]"
            type-show="number"
            v-model.number="form.sort_order"
          />
        </div>
      </app-custom-dialog>

      <app-custom-dialog
        v-model="showStoreQr"
        :footer="false"
        title="store_buffet_qr"
        width="450px"
      >
        <div slot="activator">
          <div class="qr-dialog-content">
            <div
              class="text-h5 font-weight-bold mb-4 primary--text text-center"
            >
              {{ user?.stores?.name || $t("store_buffet_menu") }}
            </div>

            <div class="qr-code-wrapper mb-4">
              <div class="qr-code-container">
                <v-img
                  :src="storeQrImage"
                  width="200"
                  height="200"
                  class="mx-auto qr-code-image"
                />
              </div>
            </div>

            <div class="mb-4">
              <v-btn
                block
                outlined
                color="primary"
                class="text-capitalize"
                @click="copyQR(storeQrImage)"
              >
                <v-icon left small>fal fa-copy</v-icon>
                {{ $t("copy") }}
              </v-btn>
            </div>

            <div>
              <v-btn
                block
                color="primary"
                class="text-capitalize"
                @click="printStoreQr"
              >
                <v-icon left small>fal fa-print</v-icon>
                {{ $t("print_qr") }}
              </v-btn>
            </div>
          </div>
        </div>
      </app-custom-dialog>

      <qr-print
        v-show="showPrintStoreQr"
        :qr="printStoreQrData.qr"
        :table-name="printStoreQrData.tableName"
        :store-name="printStoreQrData.storeName"
        :store-logo="printStoreQrData.storeLogo"
      />

      <app-loading :overlay="overlay || saving" />
    </section>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { required } from "vuelidate/lib/validators";
import { myMixin } from "@/mixin/main";
import { toast, confirm } from "@/controllers/alert";
import TTextField from "@/components/global/TextField";
import TextSelect from "@/components/global/TextSelect";
import CurrencyInput from "@/components/CurrencyInput";
import QrPrint from "@/components/print/QrPrint.vue";

export default {
  name: "ManageBuffetPlans",
  components: { TTextField, TextSelect, CurrencyInput, QrPrint },
  mixins: [myMixin],
  validations: {
    form: {
      name: { required },
    },
  },
  data() {
    return {
      loading: false,
      overlay: false,
      saving: false,
      dialog: false,
      dialogAdd: false,
      editingId: null,
      items: [],
      categoriesRaw: [],
      viewForm: {},
      form: {
        name: "",
        price: 0,
        category_ids: [],
        is_active: true,
        sort_order: 0,
      },
      showStoreQr: false,
      storeQrImage: "",
      showPrintStoreQr: false,
      printStoreQrData: {
        qr: "",
        tableName: "",
        storeName: "",
        storeLogo: null,
      },
    };
  },
  computed: {
    rowsPerPageItems() {
      return this.$store.getters["index/rowsPerPageItems"] || [15, 30, 50];
    },
    ...mapGetters({
      user: "auth/user",
    }),
    branchId() {
      const b = this.user?.branch || this.user?.branch_id;
      return b?.id ?? "";
    },
    headers() {
      return [
        { text: "#", value: "index", sortable: false, width: "52px" },
        { text: this.$t("buffet_plan_name"), value: "name" },
        { text: this.$t("buffet_plan_price"), value: "price", align: "end" },
        { text: this.$t("buffet_plan_categories"), value: "categories" },
        { text: this.$t("status"), value: "is_active", align: "center" },
        {
          text: this.$t("manage"),
          value: "actions",
          align: "end",
          sortable: false,
          width: "148px",
        },
      ];
    },
    titleStatus() {
      return this.editingId ? "update" : "new_data";
    },
    nameErrors() {
      const errors = [];
      if (!this.$v.form.name.$dirty) return errors;
      if (!this.$v.form.name.required) {
        errors.push(this.$t("validation_required"));
      }
      return errors;
    },
    categoryFieldKey() {
      const map = {
        name_en: "en_name",
        name_th: "th_name",
        name_cn: "cn_name",
        name_la: "la_name",
      };
      return map[this.localeKey] || "la_name";
    },
    /** Same shape as add-product category TextSelect: id + la_name (display follows locale). */
    categorySelectItems() {
      return (this.categoriesRaw || []).map((c) => ({
        id: c.id,
        la_name: this.getCategoryName(c) || String(c.id),
      }));
    },
  },
  async created() {
    if (this.store_type === "BUFFET") {
      await Promise.all([this.loadPlans(), this.loadCategories()]);
    }
  },
  methods: {
    openStoreQr() {
      const code = this.user?.stores?.code;
      if (!code || !this.branchId) {
        return;
      }
      const params = `${code}/${this.branchId}/store`;
      this.storeQrImage = this.StringToQRCode(params);
      this.showStoreQr = true;
    },
    printStoreQr() {
      if (!this.storeQrImage) {
        return;
      }
      const storeName = this.user?.stores?.name || "";
      const storeLogo = this.user?.stores?.logo
        ? process.env.VUE_APP_BASE_URL + "/uploads/" + this.user.stores.logo
        : null;

      this.printStoreQrData = {
        qr: this.storeQrImage,
        tableName: this.$t("store_buffet_menu"),
        storeName,
        storeLogo,
      };

      this.showStoreQr = false;
      this.showPrintStoreQr = true;

      setTimeout(() => {
        setTimeout(() => {
          this.$htmlToPaper("printQR");
          setTimeout(() => {
            this.showPrintStoreQr = false;
          }, 1000);
        }, 500);
      }, 500);
    },
    touchName() {
      this.$v.form.name.$touch();
    },
    categoryLabel(c) {
      const key = this.categoryFieldKey;
      return c[key] || c.la_name || c.id;
    },
    categorySummary(cats) {
      if (!cats || !cats.length) return "—";
      const nameKey = this.categoryFieldKey;
      return cats
        .map((c) => c[nameKey] || c.la_name || c.id)
        .slice(0, 4)
        .join(", ");
    },
    async loadCategories() {
      try {
        const { data } = await this.$http.get("categories");
        if (data && data.status && Array.isArray(data.data)) {
          this.categoriesRaw = data.data;
        }
      } catch (e) {
        this.categoriesRaw = [];
      }
    },
    async loadPlans() {
      if (!this.user?.stores?.id) return;
      this.loading = true;
      try {
        const { data } = await this.$http.get("buffet_plans", {
          params: {
            store_id: this.user.stores.id,
            branch_id: this.branchId,
          },
        });
        if (data && data.status && Array.isArray(data.data)) {
          this.items = data.data;
        } else {
          this.items = [];
        }
      } catch (e) {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    viewItem(item) {
      this.viewForm = { ...item };
      this.dialog = true;
    },
    openCreate() {
      this.editingId = null;
      this.form = {
        name: "",
        price: 0,
        category_ids: [],
        is_active: true,
        sort_order: 0,
      };
      this.$v.form.$reset();
      this.dialogAdd = true;
    },
    openEdit(item) {
      this.editingId = item.id;
      this.form = {
        name: item.name,
        price: Number(item.price) || 0,
        category_ids: (item.categories || []).map((c) => c.id),
        is_active: item.is_active !== 0 && item.is_active !== false,
        sort_order: item.sort_order ?? 0,
      };
      this.dialogAdd = true;
    },
    async save() {
      this.$v.form.$touch();
      if (this.$v.form.$invalid) {
        toast(this, "validation_required", "error");
        return;
      }
      const body = {
        store_id: this.user.stores.id,
        branch_id: this.branchId || null,
        name: String(this.form.name).trim(),
        price: Number(this.form.price) || 0,
        category_ids: this.form.category_ids || [],
        is_active: !!this.form.is_active,
        sort_order: Number(this.form.sort_order) || 0,
      };
      this.saving = true;
      try {
        let data;
        if (this.editingId) {
          const res = await this.$http.put(`buffet_plans/${this.editingId}`, {
            ...body,
          });
          data = res.data;
        } else {
          const res = await this.$http.post("buffet_plans", body);
          data = res.data;
        }
        if (data && data.status) {
          this.dialogAdd = false;
          await toast(this, data.message || "success", "success");
          await this.loadPlans();
        } else {
          await toast(this, data?.message || "error_occurred", "error");
        }
      } catch (e) {
        await toast(this, "error_occurred", "error");
      } finally {
        this.saving = false;
      }
    },
    async remove(item) {
      const { isConfirmed } = await confirm({
        props: this,
        text: "want_to_delete",
      });
      if (!isConfirmed) return;
      this.overlay = true;
      try {
        const { data } = await this.$http.delete(`buffet_plans/${item.id}`);
        if (data && data.status) {
          await toast(this, data.message || "success", "success");
          await this.loadPlans();
        }
      } finally {
        this.overlay = false;
      }
    },
  },
};
</script>

<style scoped>
.qr-dialog-content {
  text-align: center;
}

.qr-code-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background: #f5f5f5;
  border-radius: 8px;
  margin: 0 auto;
}

.qr-code-container {
  position: relative;
  display: inline-block;
}

.qr-code-image {
  display: block;
}
</style>
